import { makeAutoObservable, runInAction } from 'mobx'
import {
  AgentActionNote,
  AgentActionSendMessage,
  AgentActionTag,
  type AgentActions,
  type IAgentActionModel,
  type IAgentActionType,
} from 'entities/AgentActions'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { AgentActionMessageStore } from './agentActionMessageStore'
import { AgentActionNoteStore } from './agentActionNoteStore'
import { AgentActionTagStore } from './agentActionTagStore'

export type IAgentActionStores =
  | AgentActionTagStore
  | AgentActionNoteStore
  | AgentActionMessageStore

const storeFactory = (model: IAgentActionModel, entities: IEntitiesStore): IAgentActionStores => {
  if (model instanceof AgentActionTag) return new AgentActionTagStore(model)
  if (model instanceof AgentActionNote) return new AgentActionNoteStore(model, entities)
  if (model instanceof AgentActionSendMessage) return new AgentActionMessageStore(model, entities)
  throw new TypeError('Unknown agent action model')
}

export class AgentActionsControl {
  private _storesMap = new Map<IAgentActionModel, IAgentActionStores>()

  constructor(private _model: AgentActions, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get list() {
    return this._model.actions
  }

  get entities() {
    return this._entities
  }

  getActionStore(model: IAgentActionModel): IAgentActionStores {
    const store = this._storesMap.get(model) ?? storeFactory(model, this._entities)
    runInAction(() => this._storesMap.set(model, store))
    return store
  }

  createAction = (type: IAgentActionType) => {
    const model = this._model.addAction(type)
    const store = storeFactory(model, this._entities)
    this._storesMap.set(model, store)
  }

  removeAction = (index: number) => {
    const model = this._model.removeAction(index)
    if (!model) return
    const store = this._storesMap.get(model)
    this._storesMap.delete(model)
    store?.dispose()
  }

  dispose = () => {
    this._storesMap.forEach((store) => store.dispose())
    this._storesMap.clear()
  }
}
